import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "lucide-react";
import { LogoIcon } from "@/components/Logo";

const PAID_PLANS = ["starter", "pro", "scale"];

export default function Signup() {
  const { user, register } = useAuth();
  const [searchParams] = useSearchParams();
  const planParam = (searchParams.get("plan") || "").trim().toLowerCase();
  const afterAuthPath = PAID_PLANS.includes(planParam)
    ? `/app/plan-checkout?plan=${encodeURIComponent(planParam)}`
    : "/app/dashboard";

  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  if (user) return <Navigate to={afterAuthPath} replace />;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const res = await register(form);
    setLoading(false);
    if (res.ok) navigate(afterAuthPath);
    else setErr(res.error || "Falha ao criar conta");
  };

  return (
    <div className="min-h-screen bg-cp-bg text-slate-100 font-body flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="signup-logo-link">
          <LogoIcon size={36} />
          <span className="font-heading font-bold text-lg">ConvertPix</span>
        </Link>

        <div className="mb-8">
          <div className="font-mono text-xs uppercase tracking-widest text-cp-primary-light mb-3">_criar conta</div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter">Crie sua conta no ConvertPix</h1>
          <p className="text-slate-400 text-sm mt-2">
            Preencha os campos abaixo para cadastrar seu acesso. Usamos e-mail e senha; não pedimos cartão nesta etapa.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Nome</label>
              <input
                required
                value={form.name}
                onChange={update("name")}
                data-testid="signup-name-input"
                className="w-full bg-cp-elevated border border-white/10 focus:border-cp-primary focus:ring-2 focus:ring-cp-primary/30 outline-none text-sm px-4 py-3 rounded-sm"
                placeholder="Seu nome"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Empresa</label>
              <input
                value={form.company}
                onChange={update("company")}
                data-testid="signup-company-input"
                className="w-full bg-cp-elevated border border-white/10 focus:border-cp-primary focus:ring-2 focus:ring-cp-primary/30 outline-none text-sm px-4 py-3 rounded-sm"
                placeholder="Opcional"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">WhatsApp / celular</label>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              required
              value={form.phone}
              onChange={update("phone")}
              data-testid="signup-phone-input"
              className="w-full bg-cp-elevated border border-white/10 focus:border-cp-primary focus:ring-2 focus:ring-cp-primary/30 outline-none text-sm px-4 py-3 rounded-sm"
              placeholder="(11) 99999-9999"
            />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              data-testid="signup-email-input"
              className="w-full bg-cp-elevated border border-white/10 focus:border-cp-primary focus:ring-2 focus:ring-cp-primary/30 outline-none text-sm px-4 py-3 rounded-sm"
              placeholder="voce@empresa.com"
            />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Senha</label>
            <input
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={update("password")}
              data-testid="signup-password-input"
              className="w-full bg-cp-elevated border border-white/10 focus:border-cp-primary focus:ring-2 focus:ring-cp-primary/30 outline-none text-sm px-4 py-3 rounded-sm"
              placeholder="Mínimo 6 caracteres"
            />
          </div>

          {err && (
            <div data-testid="signup-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="signup-submit-btn"
            className="w-full bg-cp-primary hover:bg-cp-primary-hover disabled:opacity-60 text-cp-bg font-bold py-3 rounded-sm transition-all flex items-center justify-center gap-2"
          >
            {loading ? "Criando..." : <>Criar conta <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>

        <div className="text-sm text-slate-400 mt-8 text-center">
          Já tem conta?{" "}
          <Link
            to={PAID_PLANS.includes(planParam) ? `/login?plan=${encodeURIComponent(planParam)}` : "/login"}
            data-testid="signup-to-login"
            className="text-cp-primary-light hover:text-cp-primary-light font-semibold"
          >
            Entrar
          </Link>
        </div>
      </div>
    </div>
  );
}
